<template>
    <th
        :class="getClass"
        scope="col"
        @click="click($event)"
        ref="dropdown"
    >
        <div class="flex-con is-grow xs-pad-lr">
            <div class="main">
                {{name}}
                <span
                    v-if="tooltip"
                    class="tooltip is-tooltip-bottom"
                    :data-tooltip="tooltip"
                >
                    <i class="fa fa-question-circle"></i>
                </span>
            </div>
            <div
                class="is-end is-narrow"
                v-if="isActive"
            >
                <div class="icon">
                    <i
                        class="fa"
                        :class="sortIcon"
                    ></i>
                </div>
            </div>
        </div>
        <div
            v-if="showOptions"
            class="fields-con"
            @click="$event.stopPropagation()"
        >
            <div class="head flex-con is-grow xs-pad-lr">
                <div class="is-narrow">
                    <div
                        class="button is-small"
                        @click="$emit('move', { handle : handle, left : true })"
                    >
                        <i class="fa fa-angle-left"></i>
                    </div>
                </div>
                <div class="has-text-centered">
                    <div
                        class="button is-small is-danger is-uppercase"
                        @click="$emit('remove', handle)"
                    >
                        Remove
                    </div>
                </div>
                <div class="is-narrow is-end">
                    <div
                        class="button is-small"
                        @click="$emit('move', { handle : handle, right : true })"
                    >
                        <i class="fa fa-angle-right"></i>
                    </div>
                </div>
            </div>
            <div
                v-for="group in availableGroupedFields"
                :key="group.groupName"
                class="group"
            >
                <div class="group-name">{{group.groupName}}</div>
                <div class="fields">
                    <div
                        v-for="field in group.fields"
                        :key="field.handle"
                        class="flex-con xs-pad-lr is-grow"
                        :class="{ 'in-use' : columnIsDisabled(field.handle) }"
                    >
                        <div class="is-narrow">
                            <div
                                class="button is-small"
                                :class="{ 'is-static' : columnIsDisabled(field.handle) }"
                                @click="addColumn(field.handle, false)"
                            >
                                <i class="fa fa-angle-left"></i>
                            </div>
                        </div>
                        <div class="field-name">
                            {{field.nameNoPrefix}}
                        </div>
                        <div class="is-end is-narrow">
                            <div
                                class="button is-small"
                                :class="{ 'is-static' : columnIsDisabled(field.handle) }"
                                @click="addColumn(field.handle, true)"
                            >
                                <i class="fa fa-angle-right"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </th>
</template>

<script>
export default {
    name : "SortColumn",
    data () {
        return {
            showOptions : false,
        };
    },
    props : {
        name : {
            type : String,
            required : true,
        },
        handle : {
            type : String,
            required : true,
        },
        activeSort : {
            type : String,
            required : true,
        },
        sortDescending : {
            type : Boolean,
            required : true,
        },
        className : {
            type : String,
        },
        disabled : {
            type : Boolean,
            required : false,
            default : false,
        },
        availableGroupedFields : {
            type : Array,
            required : true,
        },
        activeProjectionColumns : {
            type : Array,
            required : true,
        },
        tooltip : {
            type : String,
            required : false,
            default : null
        }
    },
    computed : {
        isActive () {
            return this.activeSort === this.handle;
        },
        sortIcon () {
            return !this.sortDescending ? "fa-caret-up" : "fa-caret-down";
        },
        getClass () {
            const classes = [this.className];
            if(this.disabled){
                classes.push('disabled');
                return classes;
            }
            if(this.isActive){
                classes.push('active');
            }
            return classes;
        },
    },
    methods : {
        click (event) {
            if(event.ctrlKey || event.metaKey){
                window.addEventListener('click', this.closeFieldGroups, { passive : true })
                this.showOptions = true;
                return;
            }
            if(this.disabled){
                return;
            }
            if(!this.isActive){
                this.$emit('change:column', this.handle);
            } else {
                this.$emit('change:direction', !this.sortDescending);
            }
        },
        closeFieldGroups (event) {
            if(this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)){
                this.showOptions = false;
                window.removeEventListener('click', this.closeFieldGroups);
            }
        },
        addColumn (handle, after) {
            if(this.columnIsDisabled(handle)){
                return;
            }
            this.$emit('add:column', {
                addHandle : handle,
                fromHandle : this.handle,
                after : after,
            });
            this.showOptions = false;
            window.removeEventListener('click', this.closeFieldGroups);
        },
        columnIsDisabled (handle) {
            return this.activeProjectionColumns.indexOf(handle) !== -1;
        },
    },
}
</script>

<style lang="scss" scoped>
    @import '../../../assets/css/_theme.scss';
    th {
        cursor:pointer;
        position:relative;
        &.disabled {
            cursor:not-allowed;
        }
        &:hover,
        &.active {
            background-color:$white-bis !important;
            border-radius:5px 5px 0 0;
        }
        &:hover:after {
            border-bottom:2px solid $info !important;
        }
        &.active:after {
            border-bottom:2px solid $primary !important;
        }
    }

    .main {
        position:relative;
    }

    .fields-con {
        position: absolute;
        width:300px;
        height:500px;
        left:0;
        z-index:39;
        background-color:$white;
        overflow-y:auto;
        cursor:text;
        border:2px solid $grey-lighter;
        border-top:none;
        margin-top:.4em;
        text-align:left;

        .head {
            padding:8px 8px;
        }

        .group {
            .group-name {
                font-weight:bold;
                padding:4px 8px;
                background-color:$white-bis;
            }
            .fields {
                & > div {
                    padding:4px 8px;
                    border-bottom:1px solid $white-ter;
                    font-weight:normal;
                    &.in-use {
                        color:$grey;
                    }
                }
            }
        }
    }

</style>
